// Onboarding store — drives the wizard and owns the bridge between the
// UI, the resumable SQLite state (lib/storage/onboarding.ts), the token
// vault (lib/secrets.ts), and the GitHub client (lib/github.ts).
//
// The wizard component is thin: it renders based on `record.step` and
// calls these actions. All the "what does this step actually do"
// logic lives here so the steps stay declarative.

import { create } from "zustand";
import {
  getOnboarding, saveOnboarding, resetOnboarding,
  STEP, type Onboarding,
} from "../lib/storage/onboarding";
import { getSecret, setSecret, clearSecret } from "../lib/secrets";
import {
  validateToken, canCreateRepos, repoExists, createRepo,
  GithubError, type GithubIdentity,
} from "../lib/github";
import { privateRepoName, publicRepoName, defaultSiteUrl } from "../lib/handle";
import { setProfile } from "../lib/profile";
import { createDraft, setPublished } from "../lib/storage/drafts";
import { starterPost } from "../lib/publish";
import { publishAll } from "../lib/publish-flow";

interface OnboardingState {
  /** True while the initial hydrate from SQLite is in flight. */
  loading: boolean;
  /** The resumable record for the current identity (null = untouched). */
  record: Onboarding | null;
  /** Re-validated GitHub identity, if a token is in the vault. */
  githubIdentity: GithubIdentity | null;
  /** A step-scoped async op is running (validating, provisioning…). */
  busy: boolean;
  /** Last error, surfaced verbatim per the selectable-error discipline. */
  error: string | null;

  hydrate: (googleSub: string) => Promise<void>;
  connectGithub: (googleSub: string, token: string) => Promise<boolean>;
  disconnectGithub: (googleSub: string) => void;
  checkHandle: (googleSub: string, handle: string) => Promise<{ ok: boolean; reason?: string }>;
  chooseHandle: (googleSub: string, handle: string) => Promise<boolean>;
  provisionRepos: (googleSub: string) => Promise<boolean>;
  publishStarter: (googleSub: string, displayName: string, bio: string) => Promise<boolean>;
  finishOnboarding: (googleSub: string) => Promise<void>;
  reset: (googleSub: string) => Promise<void>;
  clearError: () => void;
}

function describe(e: unknown): string {
  if (e instanceof GithubError) return `${e.message} (GitHub ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  loading: true,
  record: null,
  githubIdentity: null,
  busy: false,
  error: null,

  hydrate: async (googleSub) => {
    set({ loading: true, error: null });
    try {
      const record = await getOnboarding(googleSub);
      let githubIdentity: GithubIdentity | null = null;
      const token = getSecret("github_token", googleSub);
      if (token) {
        // Best-effort: a stale/revoked token shouldn't block the app,
        // it just means the GitHub step needs redoing.
        try {
          githubIdentity = await validateToken(token);
        } catch {
          clearSecret("github_token", googleSub);
        }
      }
      set({ record, githubIdentity, loading: false });
    } catch (e) {
      set({ error: describe(e), loading: false });
    }
  },

  connectGithub: async (googleSub, token) => {
    set({ busy: true, error: null });
    try {
      const identity = await validateToken(token.trim());
      if (!canCreateRepos(identity)) {
        set({
          busy: false,
          error: "That token is missing the `repo` scope, so it can't create your repos. Create a new token with the `repo` scope checked.",
        });
        return false;
      }
      setSecret("github_token", googleSub, token.trim());
      const record = await saveOnboarding(googleSub, {
        step: STEP.GITHUB,
        github_login: identity.login,
      });
      set({ record, githubIdentity: identity, busy: false });
      return true;
    } catch (e) {
      const msg = e instanceof GithubError && e.status === 401
        ? "GitHub rejected that token (401). Double-check you copied the whole thing and it hasn't expired."
        : describe(e);
      set({ busy: false, error: msg });
      return false;
    }
  },

  disconnectGithub: (googleSub) => {
    clearSecret("github_token", googleSub);
    set({ githubIdentity: null });
  },

  checkHandle: async (googleSub, handle) => {
    const token = getSecret("github_token", googleSub);
    const login = get().githubIdentity?.login;
    if (!token || !login) return { ok: false, reason: "Connect GitHub first." };
    set({ busy: true, error: null });
    try {
      // Both target repos must be free on the creator's account.
      const [priv, pub] = await Promise.all([
        repoExists(token, login, privateRepoName(handle)),
        repoExists(token, login, publicRepoName(handle)),
      ]);
      set({ busy: false });
      if (priv || pub) {
        const which = priv && pub ? "Both repos" : priv ? privateRepoName(handle) : publicRepoName(handle);
        return { ok: false, reason: `${which} already ${priv && pub ? "exist" : "exists"} on your account. Pick another handle or reuse it from Settings.` };
      }
      return { ok: true };
    } catch (e) {
      set({ busy: false, error: describe(e) });
      return { ok: false, reason: describe(e) };
    }
  },

  chooseHandle: async (googleSub, handle) => {
    set({ busy: true, error: null });
    try {
      const record = await saveOnboarding(googleSub, { step: STEP.HANDLE, handle });
      set({ record, busy: false });
      return true;
    } catch (e) {
      set({ busy: false, error: describe(e) });
      return false;
    }
  },

  provisionRepos: async (googleSub) => {
    const token = getSecret("github_token", googleSub);
    const handle = get().record?.handle;
    if (!token || !handle) {
      set({ error: "Missing GitHub token or handle — go back a step." });
      return false;
    }
    set({ busy: true, error: null });
    const priv = privateRepoName(handle);
    const pub = publicRepoName(handle);
    try {
      // Create sequentially so a partial failure leaves a clear state;
      // a 422 ("already exists") on resume is treated as success.
      const created: Record<string, string> = {};
      for (const [name, isPrivate, desc] of [
        [priv, true, "BKA private working repo — drafts, scratch, media staging. Yours alone."],
        [pub, false, "BKA public archive — published posts + feed. Your face to the world."],
      ] as [string, boolean, string][]) {
        try {
          const repo = await createRepo(token, { name, description: desc, private: isPrivate });
          created[name] = repo.fullName;
        } catch (e) {
          if (e instanceof GithubError && e.status === 422) {
            // Already exists (resume / re-run) — accept it.
            const login = get().githubIdentity?.login ?? "";
            created[name] = `${login}/${name}`;
          } else {
            throw e;
          }
        }
      }
      const record = await saveOnboarding(googleSub, {
        step: STEP.REPOS,
        private_repo: created[priv],
        public_repo: created[pub],
      });
      set({ record, busy: false });
      return true;
    } catch (e) {
      set({ busy: false, error: describe(e) });
      return false;
    }
  },

  publishStarter: async (googleSub, displayName, bio) => {
    const token = getSecret("github_token", googleSub);
    const record = get().record;
    if (!token || !record?.handle || !record.public_repo) {
      set({ error: "Missing GitHub token, handle, or public repo — go back a step." });
      return false;
    }
    set({ busy: true, error: null });
    try {
      // Persist the creator's identity + the live site URL, then mint
      // the starter as a *tracked published draft* so it's a first-class
      // post the composer can later edit/unpublish — not a one-off
      // synthetic commit. publishAll() renders it from there.
      setProfile(googleSub, { displayName: displayName.trim() || record.handle, bio: bio.trim() });
      const siteUrl = defaultSiteUrl(record.handle);
      await saveOnboarding(googleSub, { step: STEP.CF_CONNECTED, cf_pages_project: siteUrl });

      const starter = starterPost(new Date().toISOString());
      const draft = await createDraft(googleSub, {
        title: starter.title, body: starter.body, summary: starter.summary,
        tags: JSON.stringify(starter.tags),
      });
      await setPublished(draft.id, starter.slug, Date.now());

      await publishAll(googleSub, "BKA: hello, world 🌱");
      const updated = await saveOnboarding(googleSub, { step: STEP.CF_CONNECTED });
      set({ record: updated, busy: false });
      return true;
    } catch (e) {
      set({ busy: false, error: describe(e) });
      return false;
    }
  },

  finishOnboarding: async (googleSub) => {
    const updated = await saveOnboarding(googleSub, { step: STEP.PUBLISHED });
    set({ record: updated });
  },

  reset: async (googleSub) => {
    await resetOnboarding(googleSub);
    clearSecret("github_token", googleSub);
    set({ record: null, githubIdentity: null, error: null });
  },

  clearError: () => set({ error: null }),
}));
